// FÁZE 2 — Scout: pool board s DnD, poznámkami/tagy, table view a pozičním filtrem.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { DownloadSimple, Kanban, Table } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { getPool, addToPool, patchPoolEntry, removePoolEntry, importPool, searchPlayers, POOL_STATUSES } from "../../lib/lab-api";
import { PlayerMiniCard } from "../../components/player/PlayerMiniCard";
import PoolNotesDialog from "../../components/lab/PoolNotesDialog";

const POSITIONS = ["ALL", "GKP", "DEF", "MID", "FWD"];

function AddPlayerSearch({ onAdded }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return; }
        const h = setTimeout(() => searchPlayers(q.trim(), 6).then((d) => setResults(d.results || [])).catch(() => {}), 250);
        return () => clearTimeout(h);
    }, [q]);
    const add = async (p) => {
        try {
            const r = await addToPool({ fpl_id: p.fpl_id, status: "watch" });
            toast.success(r.created ? `${p.name} přidán do poolu` : `${p.name} už v poolu je`);
            setQ(""); setResults([]);
            onAdded();
        } catch (e) { toast.error(e.response?.data?.detail || "Přidání selhalo"); }
    };
    return (
        <div className="relative max-w-sm flex-1">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Přidat hráče do poolu…"
                   className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] focus:outline-none focus:border-fa-mint placeholder:text-white/30"
                   data-testid="scout-add-search" />
            {results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0b0b0b] border border-fa-violet/40 z-30">
                    {results.map((p) => (
                        <button key={p.fpl_id} type="button" onClick={() => add(p)} data-testid={`scout-add-${p.fpl_id}`}
                                className="w-full text-left px-3 py-2 flex items-center justify-between border-b border-white/5 hover:bg-fa-violet/20">
                            <span className="font-display font-bold text-sm">{p.name}</span>
                            <span className="font-mono text-[9px] text-white/45">{p.team} · {p.position} · £{p.price}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function LabScout() {
    const { user, login } = useAuth();
    const { refresh } = useOutletContext();
    const [pool, setPool] = useState({ items: [], counts: {} });
    const [legacyCount, setLegacyCount] = useState(0);
    const [view, setView] = useState("board");
    const [pos, setPos] = useState("ALL");
    const [notesFor, setNotesFor] = useState(null);
    const [dragOver, setDragOver] = useState(null);

    const load = useCallback(() => {
        if (!user) return;
        getPool().then(setPool).catch(() => {});
    }, [user]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        try {
            const raw = JSON.parse(localStorage.getItem("timing.pool.v1") || "[]");
            setLegacyCount(Array.isArray(raw) ? raw.length : 0);
        } catch { setLegacyCount(0); }
    }, []);

    const doImport = async () => {
        try {
            const raw = JSON.parse(localStorage.getItem("timing.pool.v1") || "[]");
            const r = await importPool(raw.map((p) => ({ fpl_id: p.fpl_id })));
            toast.success(`Importováno ${r.imported} hráčů (${r.skipped} přeskočeno)`);
            load(); refresh();
        } catch (e) { toast.error(e.response?.data?.detail || "Import selhal"); }
    };

    const changeStatus = async (entry, status) => {
        try { await patchPoolEntry(entry.id, { status }); load(); refresh(); }
        catch { toast.error("Změna statusu selhala"); }
    };
    const remove = async (entry) => {
        try { await removePoolEntry(entry.id); toast.success(`${entry.name} odebrán`); load(); refresh(); }
        catch { toast.error("Odebrání selhalo"); }
    };

    const onDrop = (statusKey) => async (e) => {
        e.preventDefault();
        setDragOver(null);
        const id = e.dataTransfer.getData("text/pool-entry");
        const entry = pool.items.find((x) => x.id === id);
        if (entry && entry.status !== statusKey) await changeStatus(entry, statusKey);
    };

    if (!user) {
        return (
            <div className="border border-white/10 bg-black/40 p-8 text-center space-y-3" data-testid="scout-login-cta">
                <div className="font-display font-black uppercase text-base">Player Pool je per-user</div>
                <p className="font-mono text-[11px] text-white/55 max-w-md mx-auto">
                    Tvůj pool, statusy, poznámky a tagy se ukládají k tvému účtu a načtou se na všech zařízeních.
                </p>
                <button type="button" onClick={login} data-testid="scout-login-btn"
                        className="font-mono text-xs uppercase tracking-widest px-5 py-2.5 bg-fa-mint text-black font-bold">
                    Přihlásit se
                </button>
            </div>
        );
    }

    const items = pos === "ALL" ? pool.items : pool.items.filter((e) => e.position === pos);

    return (
        <div className="space-y-4" data-testid="lab-scout">
            <div className="flex flex-wrap items-center gap-3">
                <AddPlayerSearch onAdded={() => { load(); refresh(); }} />
                <div className="flex gap-1" data-testid="scout-pos-filter">
                    {POSITIONS.map((p) => (
                        <button key={p} type="button" onClick={() => setPos(p)} data-testid={`scout-pos-${p}`}
                                className={`font-mono text-[9px] uppercase px-2 py-1.5 border ${pos === p ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/50 hover:text-white"}`}>
                            {p === "ALL" ? "Vše" : p}
                        </button>
                    ))}
                </div>
                <div className="flex gap-1" data-testid="scout-view-toggle">
                    {[["board", Kanban, "Board"], ["table", Table, "Tabulka"]].map(([k, Icon, label]) => (
                        <button key={k} type="button" onClick={() => setView(k)} data-testid={`scout-view-${k}`}
                                className={`font-mono text-[9px] uppercase px-2.5 py-1.5 border flex items-center gap-1 ${view === k ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/50 hover:text-white"}`}>
                            <Icon size={11} /> {label}
                        </button>
                    ))}
                </div>
                {legacyCount > 0 && (
                    <button type="button" onClick={doImport} data-testid="scout-import-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-fa-yellow/50 text-fa-yellow flex items-center gap-1.5 hover:bg-fa-yellow hover:text-black transition-colors">
                        <DownloadSimple size={12} /> Importovat starý pool ({legacyCount})
                    </button>
                )}
            </div>

            {view === "board" ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pool-board">
                    {POOL_STATUSES.map((s) => {
                        const colItems = items.filter((e) => e.status === s.key);
                        return (
                            <div key={s.key}
                                 onDragOver={(e) => { e.preventDefault(); setDragOver(s.key); }}
                                 onDragLeave={() => setDragOver(null)}
                                 onDrop={onDrop(s.key)}
                                 className={`border p-2.5 space-y-2 min-h-[180px] transition-colors ${dragOver === s.key ? "border-fa-mint bg-fa-mint/5" : "border-white/10 bg-[#0d0d0d]"}`}
                                 data-testid={`pool-col-${s.key}`}>
                                <div className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${s.color} flex items-center justify-between`}>
                                    {s.label}
                                    <span className="tabular-nums">{colItems.length}</span>
                                </div>
                                {colItems.map((e) => (
                                    <PlayerMiniCard key={e.id} entry={e} draggable
                                                    onStatusChange={changeStatus} onRemove={remove}
                                                    onEditNotes={setNotesFor} />
                                ))}
                                {colItems.length === 0 && (
                                    <div className="font-mono text-[9px] text-white/30 text-center py-6">
                                        {dragOver === s.key ? "pusť sem" : "prázdné"}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="pool-table">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                                <th className="px-3 py-2">Hráč</th><th className="px-3 py-2">Poz</th>
                                <th className="px-3 py-2 text-right">Cena</th><th className="px-3 py-2">Klub</th>
                                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Tagy</th>
                                <th className="px-3 py-2">Poznámka</th><th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((e) => (
                                <tr key={e.id} className="border-b border-white/5 font-mono text-[11px]" data-testid={`pool-row-${e.fpl_id}`}>
                                    <td className="px-3 py-2">
                                        <Link to={`/players/${e.fpl_id}`} className="font-display font-bold hover:text-fa-mint">{e.name}</Link>
                                    </td>
                                    <td className="px-3 py-2 text-white/50">{e.position}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{e.price != null ? `£${e.price}` : "—"}</td>
                                    <td className="px-3 py-2 text-white/50">{e.team}</td>
                                    <td className="px-3 py-2">
                                        <select value={e.status} onChange={(ev) => changeStatus(e, ev.target.value)}
                                                className="bg-black/60 border border-white/15 font-mono text-[9px] uppercase px-1.5 py-1 focus:outline-none">
                                            {POOL_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex flex-wrap gap-1 max-w-[160px]">
                                            {(e.tags || []).map((t) => <span key={t} className="text-[7px] uppercase px-1 py-0.5 bg-fa-violet/20 text-fa-sky">{t}</span>)}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-white/50 text-[10px] max-w-[200px] truncate">{e.notes?.note || e.notes?.reason || ""}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2">
                                            <button type="button" onClick={() => setNotesFor(e)} className="text-fa-sky text-[10px] uppercase">Edit</button>
                                            <button type="button" onClick={() => remove(e)} className="text-white/30 hover:text-red-400 text-[10px] uppercase">×</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {items.length === 0 && <div className="font-mono text-[10px] text-white/35 text-center py-8">Pool je prázdný.</div>}
                </div>
            )}

            {notesFor && (
                <PoolNotesDialog entry={notesFor}
                                 onDone={() => { setNotesFor(null); load(); }}
                                 onCancel={() => setNotesFor(null)} />
            )}
            <p className="font-mono text-[9px] text-white/40 max-w-2xl">
                Přetáhni kartu mezi sloupci, nebo změň status selectem. Poznámky a tagy otevřeš ikonou tužky.
            </p>
        </div>
    );
}
